import { HttpClient, httpResource } from '@angular/common/http';
import { Injectable, inject, signal, computed } from '@angular/core';
import { forkJoin, of, tap, map, Observable } from 'rxjs';
import {
  type AddMember,
  type AssignRole,
  type CreateGroup,
  type GroupMember,
  type GroupResponse,
  type GroupRole,
  type GroupsResponse,
  type SetAdmin,
  type UnassignedUser,
  type UpdateGroup,
} from '@zag/auth/models';
import { SearchService } from '@zag/ui';
import { PermissionService } from './permission.service';

// Re-export shared types for convenience
export type {
  CreateGroup,
  GroupDetails,
  GroupMember,
  GroupPermission,
  GroupRole,
  ManagedGroup,
  UnassignedUser,
  UpdateGroup,
} from '@zag/auth/models';

/** Represents a member that has been added locally but not yet saved */
export interface LocalMemberAddition {
  userId: string;
  isAdmin: boolean;
  /** User info for display purposes */
  user: UnassignedUser;
}

/** Represents a role that has been added locally but not yet saved */
export interface LocalRoleAddition {
  roleId: string;
  /** Role info for display purposes */
  role: GroupRole;
}

@Injectable({ providedIn: 'root' })
export class GroupsManagementService {
  private readonly http = inject(HttpClient);
  private readonly searchService = inject(SearchService);
  private readonly permissionService = inject(PermissionService);
  private readonly basePath = '/api/v1/rbac/groups';

  // ============================================================================
  // Group List Management
  // ============================================================================

  private readonly refreshTrigger = signal(0);
  private readonly selectedRefreshTrigger = signal(0);
  readonly selectedId = signal<string | undefined>(undefined);

  private readonly listResource = httpResource<GroupsResponse>(() => {
    this.refreshTrigger();
    if (!this.permissionService.hasPermission('rbac:groups:read')) {
      return undefined;
    }
    // Track catalog search query to trigger reload when it changes
    const searchQuery = this.searchService.catalogSearchQuery();

    // Build URL with search parameter if present
    if (searchQuery) {
      return `${this.basePath}?name=${encodeURIComponent(searchQuery)}`;
    }
    return this.basePath;
  });

  private readonly selectedResource = httpResource<GroupResponse>(() => {
    const id = this.selectedId();
    this.selectedRefreshTrigger(); // Subscribe to refresh trigger
    return id ? `${this.basePath}/${id}` : undefined;
  });

  readonly groups = computed(() => this.listResource.value() ?? []);
  readonly isLoading = () => this.listResource.isLoading();
  readonly error = () => this.listResource.error();

  readonly selectedGroup = computed(() => this.selectedResource.value());
  readonly isLoadingSelected = () => this.selectedResource.isLoading();

  reload(): void {
    this.refreshTrigger.update(v => v + 1);
  }

  reloadSelected(): void {
    this.selectedRefreshTrigger.update(v => v + 1);
    // Also reload members and roles data
    this.membersRefreshTrigger.update(v => v + 1);
    this.rolesRefreshTrigger.update(v => v + 1);
  }

  selectById(id: string): void {
    this.selectedId.set(id);
    // Trigger member and role reload when group is selected
    this.membersRefreshTrigger.update(v => v + 1);
    this.rolesRefreshTrigger.update(v => v + 1);
  }

  clearSelection(): void {
    this.selectedId.set(undefined);
    this.clearLocalMemberChanges();
    this.clearLocalRoleChanges();
  }

  create(data: CreateGroup) {
    return this.http.post<GroupResponse>(this.basePath, data);
  }

  update(id: string, data: UpdateGroup) {
    return this.http.patch<GroupResponse>(`${this.basePath}/${id}`, data);
  }

  delete(id: string) {
    return this.http.delete<void>(`${this.basePath}/${id}`).pipe(
      tap(() => {
        this.reload();
      })
    );
  }

  // ============================================================================
  // Member Management - Reactive Resources
  // ============================================================================

  private readonly membersRefreshTrigger = signal(0);

  /** Resource for fetching group members */
  private readonly membersResource = httpResource<GroupMember[]>(() => {
    const groupId = this.selectedId();
    this.membersRefreshTrigger();
    return groupId ? `${this.basePath}/${groupId}/members` : undefined;
  });

  /** Resource for fetching unassigned users */
  private readonly unassignedResource = httpResource<UnassignedUser[]>(() => {
    const groupId = this.selectedId();
    this.membersRefreshTrigger();
    return groupId ? `${this.basePath}/${groupId}/members/unassigned` : undefined;
  });

  /** Backend members (already saved) */
  readonly members = computed(() => this.membersResource.value() ?? []);
  readonly membersLoading = computed(() => this.membersResource.isLoading() ?? false);
  readonly membersError = computed(() => this.membersResource.error());

  /** Unassigned users from backend */
  private readonly backendUnassignedUsers = computed(() => this.unassignedResource.value() ?? []);
  readonly unassignedLoading = computed(() => this.unassignedResource.isLoading() ?? false);

  // ============================================================================
  // Member Management - Local State for Unsaved Changes
  // ============================================================================

  /** Track members added locally (not yet saved to backend) */
  private readonly _localAddedMembers = signal<LocalMemberAddition[]>([]);

  /** Track members removed locally (not yet saved to backend) */
  private readonly _localRemovedMemberIds = signal<Set<string>>(new Set());

  /** Read-only access to local additions */
  readonly localAddedMembers = this._localAddedMembers.asReadonly();

  /** Check if there are unsaved member changes */
  readonly hasUnsavedMemberChanges = computed(() => {
    return this._localAddedMembers().length > 0 || this._localRemovedMemberIds().size > 0;
  });

  /**
   * Combined view of all members including local additions
   * Excludes members marked for removal
   */
  readonly allMembers = computed(() => {
    const backendMembers = this.members();
    const localAdded = this._localAddedMembers();
    const localRemoved = this._localRemovedMemberIds();

    // Filter out members marked for removal
    const filteredBackend = backendMembers.filter(m => !localRemoved.has(m.userId));

    // Convert local additions to GroupMember format for display
    const localAsMembers: GroupMember[] = localAdded.map(local => ({
      userId: local.userId,
      groupId: this.selectedId() ?? '',
      isAdmin: local.isAdmin,
      joinedAt: new Date().toISOString(),
      user: {
        id: local.user.id,
        name: local.user.name,
        email: local.user.email,
      },
    }));

    return [...filteredBackend, ...localAsMembers];
  });

  /**
   * Available unassigned users (excluding locally added ones)
   */
  readonly availableUnassignedUsers = computed(() => {
    const unassigned = this.backendUnassignedUsers();
    const localAddedIds = new Set(this._localAddedMembers().map(m => m.userId));
    // Also include users that were removed locally (they become available again)
    const localRemovedIds = this._localRemovedMemberIds();

    // Filter out locally added users from the unassigned list
    const filtered = unassigned.filter(u => !localAddedIds.has(u.id));

    // Add back users that were removed locally (if they're not in the filtered list)
    const backendMembers = this.members();
    const removedUsers = backendMembers
      .filter(m => localRemovedIds.has(m.userId))
      .map(m => ({
        id: m.user.id,
        name: m.user.name,
        email: m.user.email,
      }));

    // Avoid duplicates
    const existingIds = new Set(filtered.map(u => u.id));
    const additionalUsers = removedUsers.filter(u => !existingIds.has(u.id));

    return [...filtered, ...additionalUsers];
  });

  /**
   * Reload members and unassigned users
   */
  reloadMembers(): void {
    this.membersRefreshTrigger.update(v => v + 1);
  }

  /**
   * Add a member locally (not saved to backend yet)
   */
  addMemberLocally(user: UnassignedUser, isAdmin = false): void {
    const current = this._localAddedMembers();
    const removedIds = this._localRemovedMemberIds();

    // If this user was marked for removal, unmark them instead
    if (removedIds.has(user.id)) {
      const newRemoved = new Set(removedIds);
      newRemoved.delete(user.id);
      this._localRemovedMemberIds.set(newRemoved);
      return;
    }

    // Avoid duplicates
    if (!current.some(m => m.userId === user.id)) {
      this._localAddedMembers.set([...current, { userId: user.id, isAdmin, user }]);
    }
  }

  /**
   * Remove a member locally (mark for removal, not saved yet)
   * For members from backend: marks them for deletion
   * For local additions: removes them from local list
   */
  removeMemberLocally(userId: string): void {
    const localAdded = this._localAddedMembers();
    const localAddition = localAdded.find(m => m.userId === userId);

    if (localAddition) {
      // It's a local addition - just remove from local list
      this._localAddedMembers.set(localAdded.filter(m => m.userId !== userId));
    } else {
      // It's a backend member - mark for removal
      const removedIds = this._localRemovedMemberIds();
      const newRemoved = new Set(removedIds);
      newRemoved.add(userId);
      this._localRemovedMemberIds.set(newRemoved);
    }
  }

  /**
   * Clear all local member changes
   */
  clearLocalMemberChanges(): void {
    this._localAddedMembers.set([]);
    this._localRemovedMemberIds.set(new Set());
  }

  /**
   * Save all local member changes to backend
   * Returns an observable that completes when all changes are saved
   */
  saveMemberChanges(groupId: string): Observable<void> {
    const localAdded = this._localAddedMembers();
    const localRemoved = this._localRemovedMemberIds();

    if (localAdded.length === 0 && localRemoved.size === 0) {
      return of(undefined); // Nothing to save
    }

    const operations: Observable<unknown>[] = [];

    // Add new members
    for (const member of localAdded) {
      operations.push(this.addMember(groupId, member.userId, member.isAdmin));
    }

    // Remove members
    for (const userId of localRemoved) {
      operations.push(this.removeMember(groupId, userId));
    }

    return forkJoin(operations).pipe(
      tap(() => {
        this.clearLocalMemberChanges();
        this.reloadMembers();
        this.reload(); // Refresh group list to update member counts
      }),
      map(() => undefined)
    );
  }

  // ============================================================================
  // Member Management - HTTP Methods
  // ============================================================================

  getMembers(groupId: string) {
    return this.http.get<{ data: GroupMember[] }>(`${this.basePath}/${groupId}/members`);
  }

  getUnassignedUsers(groupId: string) {
    return this.http.get<{ data: UnassignedUser[] }>(`${this.basePath}/${groupId}/members/unassigned`);
  }

  addMember(groupId: string, userId: string, isAdmin = false) {
    const payload: AddMember = { userId, isAdmin };
    return this.http.post<{ message: string }>(`${this.basePath}/${groupId}/members`, payload);
  }

  removeMember(groupId: string, userId: string) {
    return this.http.delete<void>(`${this.basePath}/${groupId}/members/${userId}`);
  }

  setMemberAdmin(groupId: string, userId: string, isAdmin: boolean) {
    const payload: SetAdmin = { isAdmin };
    return this.http.put<void>(`${this.basePath}/${groupId}/members/${userId}/admin`, payload);
  }

  // ============================================================================
  // Role Management - Reactive Resources
  // ============================================================================

  private readonly rolesRefreshTrigger = signal(0);

  /** Resource for fetching group roles */
  private readonly rolesResource = httpResource<GroupRole[]>(() => {
    const groupId = this.selectedId();
    this.rolesRefreshTrigger();
    return groupId ? `${this.basePath}/${groupId}/roles` : undefined;
  });

  /** Resource for fetching unassigned roles */
  private readonly unassignedRolesResource = httpResource<GroupRole[]>(() => {
    const groupId = this.selectedId();
    this.rolesRefreshTrigger();
    return groupId ? `${this.basePath}/${groupId}/roles/unassigned` : undefined;
  });

  /** Backend roles (already saved) */
  readonly roles = computed(() => this.rolesResource.value() ?? []);
  readonly rolesLoading = computed(() => this.rolesResource.isLoading() ?? false);
  readonly rolesError = computed(() => this.rolesResource.error());

  /** Unassigned roles from backend */
  private readonly backendUnassignedRoles = computed(() => this.unassignedRolesResource.value() ?? []);
  readonly unassignedRolesLoading = computed(() => this.unassignedRolesResource.isLoading() ?? false);

  // ============================================================================
  // Role Management - Local State for Unsaved Changes
  // ============================================================================

  /** Track roles added locally (not yet saved to backend) */
  private readonly _localAddedRoles = signal<LocalRoleAddition[]>([]);

  /** Track roles removed locally (not yet saved to backend) */
  private readonly _localRemovedRoleIds = signal<Set<string>>(new Set());

  /** Read-only access to local additions */
  readonly localAddedRoles = this._localAddedRoles.asReadonly();

  /** Check if there are unsaved role changes */
  readonly hasUnsavedRoleChanges = computed(() => {
    return this._localAddedRoles().length > 0 || this._localRemovedRoleIds().size > 0;
  });

  /**
   * Combined view of all roles including local additions
   * Excludes roles marked for removal
   */
  readonly allRoles = computed(() => {
    const backendRoles = this.roles();
    const localAdded = this._localAddedRoles();
    const localRemoved = this._localRemovedRoleIds();

    // Filter out roles marked for removal
    const filteredBackend = backendRoles.filter(r => !localRemoved.has(r.id));

    // Convert local additions to GroupRole format for display
    const localAsRoles: GroupRole[] = localAdded.map(local => local.role);

    return [...filteredBackend, ...localAsRoles];
  });

  /**
   * Available unassigned roles (excluding locally added ones)
   */
  readonly availableUnassignedRoles = computed(() => {
    const unassigned = this.backendUnassignedRoles();
    const localAddedIds = new Set(this._localAddedRoles().map(r => r.roleId));
    // Also include roles that were removed locally (they become available again)
    const localRemovedIds = this._localRemovedRoleIds();

    // Filter out locally added roles from the unassigned list
    const filtered = unassigned.filter(r => !localAddedIds.has(r.id));

    // Add back roles that were removed locally (if they're not in the filtered list)
    const backendRoles = this.roles();
    const removedRoles = backendRoles.filter(r => localRemovedIds.has(r.id));

    // Avoid duplicates
    const existingIds = new Set(filtered.map(r => r.id));
    const additionalRoles = removedRoles.filter(r => !existingIds.has(r.id));

    return [...filtered, ...additionalRoles];
  });

  /**
   * Reload roles and unassigned roles
   */
  reloadRoles(): void {
    this.rolesRefreshTrigger.update(v => v + 1);
  }

  /**
   * Add a role locally (not saved to backend yet)
   */
  addRoleLocally(role: GroupRole): void {
    const current = this._localAddedRoles();
    const removedIds = this._localRemovedRoleIds();

    // If this role was marked for removal, unmark it instead
    if (removedIds.has(role.id)) {
      const newRemoved = new Set(removedIds);
      newRemoved.delete(role.id);
      this._localRemovedRoleIds.set(newRemoved);
      return;
    }

    // Avoid duplicates
    if (!current.some(r => r.roleId === role.id)) {
      this._localAddedRoles.set([...current, { roleId: role.id, role }]);
    }
  }

  /**
   * Remove a role locally (mark for removal, not saved yet)
   * For roles from backend: marks them for deletion
   * For local additions: removes them from local list
   */
  removeRoleLocally(roleId: string): void {
    const localAdded = this._localAddedRoles();
    const localAddition = localAdded.find(r => r.roleId === roleId);

    if (localAddition) {
      // It's a local addition - just remove from local list
      this._localAddedRoles.set(localAdded.filter(r => r.roleId !== roleId));
    } else {
      // It's a backend role - mark for removal
      const removedIds = this._localRemovedRoleIds();
      const newRemoved = new Set(removedIds);
      newRemoved.add(roleId);
      this._localRemovedRoleIds.set(newRemoved);
    }
  }

  /**
   * Clear all local role changes
   */
  clearLocalRoleChanges(): void {
    this._localAddedRoles.set([]);
    this._localRemovedRoleIds.set(new Set());
  }

  /**
   * Save all local role changes to backend
   * Returns an observable that completes when all changes are saved
   */
  saveRoleChanges(groupId: string): Observable<void> {
    const localAdded = this._localAddedRoles();
    const localRemoved = this._localRemovedRoleIds();

    if (localAdded.length === 0 && localRemoved.size === 0) {
      return of(undefined); // Nothing to save
    }

    const operations: Observable<unknown>[] = [];

    // Add new roles
    for (const roleAddition of localAdded) {
      operations.push(this.assignRole(groupId, roleAddition.roleId));
    }

    // Remove roles
    for (const roleId of localRemoved) {
      operations.push(this.removeRole(groupId, roleId));
    }

    return forkJoin(operations).pipe(
      tap(() => {
        this.clearLocalRoleChanges();
        this.reloadRoles();
        this.reload(); // Refresh group list to update role counts
      }),
      map(() => undefined)
    );
  }

  // ============================================================================
  // Role Management - HTTP Methods
  // ============================================================================

  getRoles(groupId: string) {
    return this.http.get<{ data: GroupRole[] }>(`${this.basePath}/${groupId}/roles`);
  }

  getUnassignedRoles(groupId: string) {
    return this.http.get<{ data: GroupRole[] }>(`${this.basePath}/${groupId}/roles/unassigned`);
  }

  assignRole(groupId: string, roleId: string) {
    const payload: AssignRole = { roleId };
    return this.http.post<{ message: string }>(`${this.basePath}/${groupId}/roles`, payload);
  }

  removeRole(groupId: string, roleId: string) {
    return this.http.delete<void>(`${this.basePath}/${groupId}/roles/${roleId}`);
  }
}
